import {
  ComparatorContext,
  useCurrentStepIndex,
  useField,
  usePage,
  useWizardService,
} from '@kizi/comparator-core';
import {
  Button,
  Div,
  DivProps,
  Image,
  Label,
  P,
  Span,
} from '@kizi/core-components';
import { Checkbox, IconV2, IconV2Props } from '@kizi/shared-components';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Desktop, Mobile } from '../../utils/breakpoint';
import { formatPrice, isMobile } from '../../utils/global';
import {
  EnergyProduct,
  PriceMode,
  getClassesByState,
  getProductProps,
  onClick,
} from '../../utils/product';
import { Price, PriceV2 } from '../Price';
import { ProductOverviewModal } from '../ProductOverviewModal';
import { ProductUsps } from '../ProductUsps';
import { PromotionBar, PromotionBarProps } from '../PromotionBar';
import { ProductSkeletonCard } from '../Skeletons/ProductSkeleton';
import { getDisplayedScore } from '../Sustainability';
import { sendCustomEvent } from '../wrappers/TabViewWizard/ApplyPageWizard';
import { ExpandedPart } from './ExpandedPart';

export interface Field {
  bold?: boolean;
  icon?: string;
  label: string | React.ReactNode;
  noRightField?: boolean;
  value?: string | React.ReactNode;
  valueIcon?: boolean;
  component?: React.ReactNode;
  tooltip?: string;
}

export interface ProductProps extends DivProps {
  data: EnergyProduct;
  priceMode: PriceMode;
  promotionModals?: PromotionBarProps;
  nextIndex?: number;
}

export interface ArrowProps extends Omit<IconV2Props, 'name'> {
  isOpen: boolean;
}

export const Arrow = ({ isOpen, ...restProps }: ArrowProps): JSX.Element => {
  const name = `big-arrow-${isOpen ? 'up' : 'down'}`;
  return <IconV2 name={name} height={16} width={16} {...restProps} />;
};

const CompareCheckboxMobile = ({ onMobileCompare, data }) => {
  return (
    <Mobile>
      <Checkbox
        mr={'8px'}
        variant="main"
        label="Vergelijk"
        id={`compare-id-${data?.id}`}
        labelPosition="right"
        containerProps={{
          fontFamily: "'Segoe UI', sans-serif",
          marginTop: '8px',
        }}
        checked={data.compare}
        onChange={() => {
          onMobileCompare?.(data.id);
        }}
      />
    </Mobile>
  );
};

export const NonPurchasableText = ({
  type,
  variant = 'unavailablePriceContainer',
  css = {},
  ...rest
}: {
  type?: any;
  variant?: string;
  css?: any;
}): JSX.Element => {
  return (
    <Div
      variant={variant}
      mt={type === 'health' && isMobile() ? 0 : null}
      maxWidth={type === 'health' ? 'unset' : null}
      css={css}
      {...rest}
    >
      <P variant="secondaryCaptionBold">
        {type && type === 'home'
          ? 'Deze verzekering kun je niet bij ons afsluiten'
          : 'Op dit moment niet af te sluiten'}
      </P>
    </Div>
  );
};

export const Product: React.FC<ProductProps> = ({
  data,
  priceMode,
  promotionModals,
  nextIndex,
  ...restProps
}) => {
  const [expandedPartStartIndex, setExpandedPartStartIndex] = useState(0);
  const [expandedPartSelectedIndex, setExpandedPartSelectedIndex] = useState(0);
  const [currentModalTab, setCurrentModalTab] = useState(-1);
  const [isOpen, setIsOpen] = useState(false);

  const { state } = usePage();

  const { wrapperClass, toggleLabelDestkop } = getClassesByState(isOpen);

  const {
    image,
    initialValue,
    discount,
    id,
    name,
    priceLabel,
    purchasableProduct,
    sustainabilityScore,
    usps,
    total,
  } = getProductProps(data, priceMode);

  const service = useWizardService();

  if (!service || !service.state.context.steps) return null;

  const overviewIndex = service.state.context.steps.findIndex(
    (step) => step.id === 'overview',
  );
  const interpreter = service.state.context.steps[overviewIndex];
  const onSubmitClick = () => onClick(interpreter, service, data, nextIndex);

  const PriceButtonComp = (
    <Div display="flex" flexDirection="column">
      <Price
        discount={discount}
        initialValue={initialValue}
        label={priceLabel}
        value={total}
        variant="priceBig"
        activeDiscount={false}
      />
      {purchasableProduct ? (
        <Button
          onClick={onSubmitClick}
          className="buildButton"
          variant="primarySmall"
        >
          {'Ga verder'}
        </Button>
      ) : (
        <NonPurchasableText />
      )}
    </Div>
  );

  const sustainabilityValue = sustainabilityScore
    ? String(Number(sustainabilityScore).toFixed(1))
    : '';

  let sustainabilityDisplayScore = sustainabilityValue;
  if (!sustainabilityValue || sustainabilityValue === '0.0') {
    sustainabilityDisplayScore = '-,-';
  }

  const setExpandedPart3 = () => {
    if (isOpen) {
      setExpandedPartSelectedIndex(3);
    } else {
      setIsOpen(true);
      setExpandedPartStartIndex(3);
    }
  };
  const setModalTab0 = () => setCurrentModalTab(0);
  const setModalTab1 = () => setCurrentModalTab(1);
  const setModalTab2 = () => setCurrentModalTab(2);
  const setModalTab3 = () => setCurrentModalTab(3);
  const resetModalTab = () => setCurrentModalTab(-1);
  const toggleOpenState = () => {
    if (!isOpen) {
      sendCustomEvent('QuoteSelected', {
        price: total,
        provider: name,
      });
    }
    setExpandedPartStartIndex(0);
    setExpandedPartSelectedIndex(0);
    setIsOpen(!isOpen);
  };

  const onCompareChange = state?.onCompareChange as Function;

  const onMobileCompare = (productId: string) => {
    if (window.matchMedia('(max-width: 767px)').matches) {
      // The viewport is less than 768 pixels wide
      onCompareChange(null, productId);
    }
  };

  const variant = '';

  const desktopCompareCheck = () => {
    return (
      <Desktop>
        <Div display="flex" alignItems="center" justifyContent="left" mt={[2]}>
          <Checkbox
            mr={2}
            variant="main"
            label="Vergelijk"
            id="compare-id"
            labelPosition="right"
            containerProps={{
              fontFamily: "'Segoe UI', sans-serif",
            }}
            checked={data.compare ? true : false}
            onChange={(e) => {
              onCompareChange(e, id);
            }}
          />
        </Div>
      </Desktop>
    );
  };

  return (
    <Div position="relative" {...restProps}>
      <Div
        data-testid="product-card"
        className={wrapperClass}
        display="flex"
        flexDirection="column"
        variant={'product'}
        pb={[0, null, 2]}
        css={{
          outline: data.compare ? '2px solid #004DF5' : '',
        }}
      >
        {promotionModals && purchasableProduct && (
          <PromotionBar items={promotionModals.items} />
        )}
        <Div
          px={[3, null, 5]}
          pt={[3, null, 5]}
          pb={[3, null, 0]}
          display="flex"
          flexDirection={['column', 'row']}
        >
          <Div
            className="alwaysVisible"
            display={[null, null, 'flex']}
            width="100%"
          >
            <Div
              display="flex"
              justifyContent="space-between"
              variant={variant}
            >
              <Div
                display="flex"
                variant="productLeftCol"
                flexDirection="column"
                justifyContent={'space-between'}
              >
                <Div
                  display="flex"
                  variant="productLogoWrapper"
                  mb={[2, null, 5]}
                >
                  <Image
                    alt={`logo-${name}`}
                    height="100%"
                    src={image}
                    width="100%"
                  />
                </Div>

                {desktopCompareCheck()}

                <CompareCheckboxMobile
                  data={data}
                  onMobileCompare={onMobileCompare}
                />
              </Div>
              <Div display={['flex', null, 'none']} flexDirection="column">
                <PriceV2
                  price={formatPrice(total)}
                  mainPriceProps={{ fontSize: '36px' }}
                  centsProps={{ fontSize: '20px', alignSelf: 'flex-end' }}
                />

                <P
                  variant="baseBody"
                  fontSize={'11px'}
                  color={'#9B9B9B'}
                  textAlign={'right'}
                  mt="6px"
                >
                  {priceLabel}
                </P>
              </Div>
            </Div>
            <Div
              display="flex"
              gridGap="1rem"
              justifyContent="space-between"
              variant={variant}
              flexDirection={['column', null, 'row']}
            >
              <Div className="leftCol" display={['initial', null, 'initial']}>
                {usps && <ProductUsps data={usps} onItemClick={setModalTab0} />}
              </Div>

              <Div
                className="rightCol"
                display="flex"
                justifyContent="space-between"
                flexDirection={'row'}
                variant="productRightCol"
              >
                {sustainabilityValue && (
                  <Div
                    onClick={setExpandedPart3}
                    css={{
                      display: 'block !important',
                      padding: '5px 10px',
                      backgroundColor: '#F2F2F0',
                      borderRadius: '4px',
                      cursor: 'pointer',
                    }}
                  >
                    <P
                      variant="baseBody"
                      css={{
                        fontSize: '20px',
                        lineHeight: '28px',
                        fontWeight: 700,
                      }}
                    >
                      {getDisplayedScore(sustainabilityDisplayScore)}
                    </P>
                    <P
                      variant="baseBody"
                      css={{
                        fontSize: '12px',
                        lineHeight: '16px',
                        marginTop: '5px',
                      }}
                    >
                      Duurzaam
                    </P>
                  </Div>
                )}
              </Div>
            </Div>
            <Div>
              <Div
                display={['block', null, 'none']}
                width="100%"
                justifyContent="space-between"
              >
                {purchasableProduct ? (
                  <Button
                    onClick={onSubmitClick}
                    className="buildButton"
                    variant="primarySmall"
                    css={{
                      width: '100%',
                      padding: '8px 24px',
                      fontSize: '16px',
                      marginTop: 0,
                    }}
                  >
                    {'Ga verder'}
                  </Button>
                ) : (
                  <NonPurchasableText
                    css={{
                      textAlign: 'center' as any,
                      marginTop: 0,
                      maxWidth: 'unset',
                    }}
                  />
                )}

                <Div
                  data-testid="toggle-product-mobile"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleOpenState();
                  }}
                  textAlign={'center'}
                  mt={'8px'}
                >
                  <P
                    variant="baseBody"
                    css={{
                      fontSize: '14px',
                      lineHeight: '21px',
                      color: '#004DF5',
                    }}
                  >
                    {isOpen ? 'Sluiten' : 'Meer informatie'}
                  </P>
                </Div>
              </Div>
              <Div
                display={['none', null, 'flex']}
                flexDirection="column"
                variant={variant}
              >
                {PriceButtonComp}
              </Div>
            </Div>
          </Div>
        </Div>
        {isOpen && (
          <ExpandedPart
            data={data}
            onTabChange={(i) => setExpandedPartSelectedIndex(i)}
            priceMode={priceMode}
            setModalTab0={setModalTab0}
            setModalTab1={setModalTab1}
            setModalTab2={setModalTab2}
            setModalTab3={setModalTab3}
            selectedTabIndex={expandedPartSelectedIndex}
            startIndex={expandedPartStartIndex}
            purchasableProduct={purchasableProduct}
          />
        )}
        <ProductOverviewModal
          startIndex={currentModalTab}
          onClose={(e) => {
            e.stopPropagation();
            resetModalTab();
          }}
          priceMode={priceMode}
          product={data}
          onSubmit={onSubmitClick}
        />
      </Div>
      <Div position="relative">
        <Button
          data-testid="toggle-product-desktop"
          display={['none', null, 'flex']}
          mx="auto"
          mt="-15px"
          onClick={toggleOpenState}
          variant="moreInformation"
        >
          {toggleLabelDestkop}
        </Button>
      </Div>
    </Div>
  );
};

interface ProductsProps extends Omit<ProductProps, 'data'> {
  productList: any;
  promotionBarModals?: PromotionBarProps;
  defaultFilters: string;
  nextIndex: number;
}

export interface ResetFiltersBlockProps {
  onResetFilters: () => void;
}

export const ResetFiltersBlock = React.memo<ResetFiltersBlockProps>(
  ({ onResetFilters }) => {
    const { name } = useContext(ComparatorContext);
    return (
      <Div
        display="flex"
        flexDirection="column"
        alignItems="center"
        marginTop={7}
      >
        <Div marginBottom={3}>
          <Label fontSize={5}>Geen resultaten gevonden</Label>
        </Div>
        <Div marginBottom={6}>
          <Span fontSize={3}>
            {`Pas je filters aan ${name === 'os-savings' ? '' : 'of reset je filters'} en probeer het opnieuw`}
          </Span>
        </Div>
        <Div>
          {['os-savings'].includes(name) ? null : (
            <Button variant="primary" onClick={onResetFilters}>
              Filters resetten
            </Button>
          )}
        </Div>
      </Div>
    );
  },
);

export function addMoreProducts(
  productsLength,
  visibleProductsCount,
  setVisibleProductsCount,
) {
  if (productsLength > visibleProductsCount + 10) {
    setVisibleProductsCount(visibleProductsCount + 10);
  } else {
    setVisibleProductsCount(
      visibleProductsCount + (productsLength - visibleProductsCount),
    );
  }
}

export const Products: React.FC<ProductsProps> = ({
  priceMode = 'month',
  productList = [],
  defaultFilters,
  promotionBarModals,
  ...restProps
}) => {
  const { state } = usePage();
  const [visibleProductsCount, setVisibleProductsCount] = useState(0);

  const currentStepIndex = useCurrentStepIndex();
  const service = useWizardService();

  const filterFields = useField(defaultFilters);
  if (!filterFields || !state) return null;

  if (
    !service ||
    !service.state.context.steps ||
    currentStepIndex === undefined
  )
    return null;
  const interpreter = service.state.context.steps[currentStepIndex];

  function handleFilterReset() {
    const filtersIndex = interpreter
      .getSnapshot()
      ?.context.fields.findIndex((field) => field.name === 'filters');

    if (filtersIndex !== undefined && filtersIndex > -1) {
      service?.send([
        {
          type: 'CHANGE',
          target: {
            name: 'filters',
            value: filterFields?.value as string,
          },
        },
      ]);
    }

    const _onChange = state?.onChange as Function;
    const _filterFieldsValue = filterFields?.value as string;
    _onChange && _onChange(JSON.parse(_filterFieldsValue));
  }

  const products = useMemo(
    () =>
      productList.map((p, i) => {
        const isFirst = i === 0;

        const _props: ProductProps = {
          data: p,
          ...restProps,
          mt: [4, 30],
          priceMode,
        };

        if (isFirst) {
          _props.mt = 0;
        }
        return (
          <Product
            key={'product-' + p.id}
            promotionModals={promotionBarModals}
            {..._props}
          />
        );
      }),
    [productList, priceMode],
  );

  const productsLength: number = products.length;

  useEffect(() => {
    setVisibleProductsCount(productsLength >= 10 ? 10 : productsLength);
  }, [productList]);

  return productsLength == 0 ? (
    <ResetFiltersBlock onResetFilters={handleFilterReset} />
  ) : (
    <>
      {Array.from(Array(visibleProductsCount).keys()).map((i) => {
        return products[i];
      })}
      <Div
        width={'fit-content'}
        mx={'auto'}
        mt={7}
        hidden={visibleProductsCount >= productsLength}
      >
        <Button
          variant={'secondaryTransparent'}
          onClick={() =>
            addMoreProducts(
              productsLength,
              visibleProductsCount,
              setVisibleProductsCount,
            )
          }
        >
          Toon meer producten
        </Button>
      </Div>
    </>
  );
};

export interface ProductsWrapperProps extends ProductsProps {
  filters: string;
  defaultFilters: string;
  disableLoadingSkeleton?: boolean;
}

export const ProductsWrapper: React.FC<ProductsWrapperProps> = ({
  filters,
  defaultFilters,
  disableLoadingSkeleton = false,
  ...restProps
}) => {
  const { state } = usePage();
  const filtersField = useField(filters);
  if (!filtersField || !filtersField.hasOwnProperty('value')) return null;
  const _filters = JSON.parse(filtersField.value as string);
  if (!_filters) return null;

  if (!disableLoadingSkeleton && state?.productsLoading !== false) {
    return (
      <>
        {new Array(5).fill(null).map((_, idx) => (
          <ProductSkeletonCard key={`loader-${idx}`} />
        ))}
      </>
    );
  }

  return (
    <Products
      {...restProps}
      {..._filters}
      defaultFilters={defaultFilters}
      productList={(state?.productList as any[]) ?? []}
      priceMode={_filters['showPriceMode'][0]}
    />
  );
};

export default ProductsWrapper;
